import { createStructureStatementHandler } from '../../createStructureStatementHandler';
import { isEventPosition, isEventType } from '../../helpers';
export const defineClickEventStmtInstruction = `
#### Defining Click Events

Click events in an application enable users to interact with the interface through buttons or clickable elements. These events are crucial for performing actions like navigating between views, deleting records, or saving changes. Below is a detailed explanation of how to define such events:

##### Event Types:

- Navigate: Redirects the user to a different view within the application.
- Delete: Removes the current entity or record from the application.
- Persist: Saves any unsaved changes made in the current view or scope.

##### Event Positions:

-   **Actions:** Indicates that the button or clickable element associated with the event will be placed in a designated action area of the application, typically found in the top right corner of the interface.
-   **Entity:** Signifies that the button will be positioned near the entity it affects, providing a contextual action for that specific item.

##### Syntax for Defining a Click Event:

\`define click_event <event_name> "<event_label>" on <source_view>[.<source_scope>] <event_type> in <event_position>[ to <target_view>];\`

-   \`<event_name>\`: A unique identifier for the event.
-   \`<event_label>\`: The text displayed on the button or clickable element.
-   \`<source_view>\`: The view where the event is triggered.
-   \`<source_scope>\`: The scope where the event is triggered.
-   \`<event_type>\`: The type of action performed by the event (\`navigate\`, \`delete\`, \`persist\`).
-   \`<event_position>\`: The position of the button or element in the UI (\`actions\`, \`entity\`).
-   \`[to <target_view>]\`: Optional. Specifies the target view to navigate to, applicable only for \`navigate\` events.

##### Examples:

-   **Navigate Event:** Directs the user to a detailed view of a product.
    \`define click_event ProductDetailNavigate "View Details" on ProductList navigate in entity to ProductDetailView;\`

-   **Delete Event:** Removes a product from the list.
    \`define click_event ProductDelete "Delete Product" on ProductList delete in entity;\`

-   **Persist Event:** Saves changes made in the current view.
    \`define click_event SaveChanges "Save Changes" on EditProduct persist in actions;\`

Note: Back button is included by default in the navigation bar of the application.

By following this detailed guide, you can accurately define interactive elements within your application, ensuring a dynamic and user-friendly experience. This clarity and specificity in your event definitions help LLMs generate the most effective and accurate code or instructions based on your application's requirements.
`;
export const stmtDefineClickEvent = (stmt) => {
    const targetView = stmt.targetViewName ? ` to ${stmt.targetViewName}` : '';
    if (!stmt.sourceScopeName) {
        return `define click_event ${stmt.eventName} "${stmt.eventLabel}" on ${stmt.sourceViewName} ${stmt.eventType} in ${stmt.eventPosition}${targetView};`;
    }
    return `define click_event ${stmt.eventName} "${stmt.eventLabel}" on ${stmt.sourceViewName}.${stmt.sourceScopeName} ${stmt.eventType} in ${stmt.eventPosition}${targetView};`;
};
export const defineClickEventHandler = createStructureStatementHandler({
    type: 'define click_event',
    parse: line => {
        const match = /^define click_event (?<eventName>\w+) "(?<eventLabel>[\w\s]+)" on (?<viewName>\w+)(\.(?<scopeName>\w+))? (?<eventType>\w+) in (?<eventPosition>\w+)( to (?<targetViewName>\w+))?/.exec(line);
        return match?.groups
            ? {
                type: 'define click_event',
                eventName: match.groups.eventName,
                eventLabel: match.groups.eventLabel,
                sourceViewName: match.groups.viewName,
                sourceScopeName: match.groups.scopeName,
                eventType: match.groups.eventType,
                eventPosition: match.groups.eventPosition,
                targetViewName: match.groups.targetViewName,
            }
            : null;
    },
    apply: (stmt, builder) => {
        builder.updateStructure(it => it.addEvent({
            eventName: stmt.eventName,
            eventLabel: stmt.eventLabel,
            sourceViewName: stmt.sourceViewName,
            sourceScopeName: stmt.sourceScopeName,
            eventType: isEventType(stmt.eventType) ? stmt.eventType : undefined,
            eventPosition: isEventPosition(stmt.eventPosition) ? stmt.eventPosition : undefined,
            targetViewName: stmt.targetViewName,
        }));
    },
});
//# sourceMappingURL=DefineClickEvent.js.map